import { css } from '@emotion/react'
import { memo, useCallback, useState } from 'react'
import {
    openSignupDrawer,
    openTokensDrawer,
} from '../../../../app-state/modals/state.drawer'
import type { IUserImage } from '../../../../mfcLogic/api/chatImages'
import {
    isLevelBasic,
    isLevelGuest,
    isLevelPremiumOrHigher,
} from '../../../../mfcLogic/userProperties/level'
import i18n from '../../../../utilities/i18n'
import Button from '../../../Buttons/Button'
import ButtonDialogClose from '../../../Buttons/ButtonDialogClose'
import { useUserSelector } from '../../../Hooks/useUser'
import { StyledTab, StyledTabs } from '../../../common/Tabs'
import { getThemeSpacing } from '../../../styles/appTheme'
import Emojis from '../../ChatImages/Emojis'
import Gifs from '../../ChatImages/Gifs'
import ImageUpload from '../../ChatImages/ImageUpload'

const tabsRootStyles = css({
    height: '100%',
    width: '100%',
    /** to position `ListGridPreviewToolbar` relative to its container */
    transform: 'translateZ(0)',
})
const tabsStyles = css({
    display: 'flex',
    overflow: 'auto',
})
const tabStyles = css({
    textTransform: 'capitalize',
})

const onlyPremiumStyles = css({
    height: '100%',
    width: '67%',
    margin: '0 auto',
    padding: getThemeSpacing(3) + ' 0',
    textAlign: 'center',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
})

const noAuthStyles = {
    marginTop: getThemeSpacing(2),
    marginBottom: getThemeSpacing(1),
}

interface EmojiPickerProps {
    onInsertEmoticon(key: string): void
    onInsertImage(imageData: IUserImage): void
    onClose(): void
    showSearch?: boolean
    fullWindow?: boolean
}

const EmojiPicker = memo((props: EmojiPickerProps) => {
    const [activeTab, setActiveTab] = useState('emojis')
    const level = useUserSelector('me', 'level')
    const isGuest = isLevelGuest(level)
    const isBasic = isLevelBasic(level)
    const isPremiumOrHigher = isLevelPremiumOrHigher(level)
    const [searchQuery, setSearchQuery] = useState('')
    const {
        onInsertEmoticon,
        onInsertImage,
        onClose,
        showSearch = true,
    } = props

    const updateQuery = (query: string) => {
        setSearchQuery(query)
    }

    const insertImage = useCallback(
        (stagedImage: IUserImage) => {
            onClose()
            onInsertImage(stagedImage)
        },
        [onClose, onInsertImage],
    )

    const onBuyTokens = useCallback(() => {
        onClose()
        openTokensDrawer()
    }, [onClose])

    const onSignup = useCallback(() => {
        onClose()
        openSignupDrawer()
    }, [onClose])

    const onClickEmoticon = useCallback(
        (key: string) => {
            if (typeof onInsertEmoticon === 'function') {
                onInsertEmoticon(key)
            }
            onClose()
        },
        [onInsertEmoticon, onClose],
    )

    const handleChange = useCallback((_, newValue) => {
        setActiveTab(newValue)
    }, [])

    const renderTabs = () => {
        return (
            <div data-testid="Container__EmojisImages" css={tabsRootStyles}>
                <StyledTabs
                    value={activeTab}
                    css={tabsStyles}
                    onChange={handleChange}
                    textColor="primary"
                    indicatorColor="primary"
                >
                    <StyledTab
                        value="emojis"
                        label={i18n.t('chatImages.emojis')}
                        css={tabStyles}
                        data-testid="Tab__Emojis"
                    />
                    <StyledTab
                        value="images"
                        css={tabStyles}
                        label={i18n.t('chatImages.images')}
                        data-testid="Tab__Images"
                    />
                    <StyledTab
                        value="upload"
                        css={tabStyles}
                        label={i18n.t('chatImages.upload')}
                        data-testid="Tab__Upload"
                    />
                </StyledTabs>
                {activeTab === 'emojis' && (
                    <Emojis
                        initialValue={searchQuery}
                        onInsertEmoticon={onClickEmoticon}
                        onQueryUpdate={updateQuery}
                        showSearch={showSearch}
                    />
                )}
                {activeTab === 'images' && (
                    <Gifs
                        initialValue={searchQuery}
                        onInsertImage={insertImage}
                        onQueryUpdate={updateQuery}
                        showSearch={showSearch}
                    />
                )}
                {activeTab === 'upload' && (
                    <ImageUpload onInsertImage={onClickEmoticon} />
                )}
                <ButtonDialogClose
                    onClick={onClose}
                    embedClose={true}
                    zeroedOut={true}
                />
            </div>
        )
    }
    return isPremiumOrHigher ? (
        renderTabs()
    ) : (
        <div css={onlyPremiumStyles}>
            <ButtonDialogClose
                onClick={onClose}
                embedClose={true}
                zeroedOut={true}
            />
            {i18n.t('chatImages.onlyPremiumMembersCanAddImages')}
            {isGuest && (
                <div style={noAuthStyles}>
                    <Button
                        data-testid="Button__CreateAccount"
                        onClick={onSignup}
                        size="medium"
                    >
                        {i18n.t('chat.createAnAccount')}
                    </Button>
                </div>
            )}
            {isBasic && (
                <div style={noAuthStyles}>
                    <Button onClick={onBuyTokens} size="medium">
                        {i18n.t('chat.purchaseTokens')}
                    </Button>
                </div>
            )}
        </div>
    )
})

export default EmojiPicker
